import { useLayoutEffect } from 'react';
import { adjustLightness, isValidHexColor } from '@/lib/color';

/**
 * White-label theming inputs (subset of BrandingData). Null/empty fields are no-ops so the default
 * OptiSigns look is preserved on non-white-label accounts.
 */
export interface BrandingThemeInput {
  colorTheme?: string | null;
  siteTitle?: string | null;
  favicon?: string | null;
}

const ROOT_PRIMARY_VAR = '--color-primary';
// Derived variants — set alongside the base so hover/active states track the brand color.
// Factors mirror scio2's setColor() (branding.service.ts): subtle lightens, hover darkens.
const PRIMARY_HOVER_VAR = '--color-primary-hover';
const PRIMARY_SUBTLE_VAR = '--color-primary-subtle';

/**
 * Apply a customer's branding to the document: portal primary color (CSS custom properties on
 * `<html>`), browser tab title, and favicon. Ported from scio2 `branding.service.ts` `setColor()`,
 * mapped onto scio3's `--color-*` tokens.
 *
 * Color is applied in `useLayoutEffect` (pre-paint) to minimize flash; title/favicon are cosmetic.
 * Invalid/empty `colorTheme` leaves the default `#1ab394` untouched (no throw).
 */
export function useBrandingTheme(branding: BrandingThemeInput | null | undefined): void {
  const colorTheme = branding?.colorTheme;
  const siteTitle = branding?.siteTitle;
  const favicon = branding?.favicon;

  // Primary color — pre-paint so we don't flash OptiSigns teal then swap.
  useLayoutEffect(() => {
    if (!isValidHexColor(colorTheme)) return;
    const root = document.documentElement.style;
    root.setProperty(ROOT_PRIMARY_VAR, colorTheme);
    root.setProperty(PRIMARY_HOVER_VAR, adjustLightness(colorTheme, 0.85));
    root.setProperty(PRIMARY_SUBTLE_VAR, adjustLightness(colorTheme, 1.6));
    return () => {
      root.removeProperty(ROOT_PRIMARY_VAR);
      root.removeProperty(PRIMARY_HOVER_VAR);
      root.removeProperty(PRIMARY_SUBTLE_VAR);
    };
  }, [colorTheme]);

  // Browser tab title.
  useLayoutEffect(() => {
    if (!siteTitle) return;
    const previous = document.title;
    document.title = siteTitle;
    return () => {
      document.title = previous;
    };
  }, [siteTitle]);

  // Favicon (expects a resolved URL — resolveBranding() already builds S3 URLs).
  useLayoutEffect(() => {
    if (!favicon) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const created = !link;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const previousHref = link.href;
    link.href = favicon;
    return () => {
      if (created) link?.remove();
      else if (link) link.href = previousHref;
    };
  }, [favicon]);
}
